import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Button, 
  Switch, 
  Upload, 
  Space,
  Modal,
  message,
  Spin,
  Popconfirm,
  List,
  Divider,
  Tooltip,
  Empty,
} from 'antd';
import { 
  SaveOutlined, 
  CloseOutlined, 
  PlusOutlined,
  UploadOutlined,
  QuestionCircleOutlined,
  ShoppingOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { scrollToFirstError } from '../../utils/formHelpers';
import { FormSkeleton } from '../../components/SkeletonLoaders';
import { productsApi, productCategoriesApi, productSubCategoriesApi, show422Error, ValidationError } from '../../utils/api';
import { parseProductTaxIds, buildProductPayload, getUploadFile, getUploadFiles } from '../../utils/productUtils';

const { Option } = Select;
const { TextArea } = Input;

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  
  // Modal states
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [subcategoryModalVisible, setSubcategoryModalVisible] = useState(false);
  const [taxModalVisible, setTaxModalVisible] = useState(false);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [subcategorySubmitting, setSubcategorySubmitting] = useState(false);
  
  // Edit states for category/subcategory management
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editCategorySaving, setEditCategorySaving] = useState(false);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [editingSubcategoryName, setEditingSubcategoryName] = useState('');
  const [editSubcategorySaving, setEditSubcategorySaving] = useState(false);
  const [loadingCategoryList, setLoadingCategoryList] = useState(false);
  const [loadingSubcategoryList, setLoadingSubcategoryList] = useState(false);
  const [categoryListForModal, setCategoryListForModal] = useState([]);
  const [subcategoryListForModal, setSubcategoryListForModal] = useState([]);
  
  // Form states for modals
  const [categoryForm] = Form.useForm();
  const [subcategoryForm] = Form.useForm();
  const [taxForm] = Form.useForm();
  
  // Data from API
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [imageFileList, setImageFileList] = useState([]);
  const [downloadableFileList, setDownloadableFileList] = useState([]);
  const [existingDownloadFile, setExistingDownloadFile] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [deleteDownloadableMarked, setDeleteDownloadableMarked] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  
  // Watch downloadable field for real-time update
  const isDownloadable = Form.useWatch('downloadable', form);

  // بارگذاری داده‌های محصول برای ویرایش
  useEffect(() => {
    if (id) {
      fetchProductData();
    }
  }, [id]);

  const fetchProductData = async () => {
    setFetching(true);
    try {
      const res = await productsApi.getById(id);
      
      if (res?.status === 'success' && res?.data) {
        const product = res.data.product;
        const formData = {
          name: product.name,
          price: parseFloat(product.price || 0),
          category_id: product.category_id,
          sub_category_id: product.sub_category_id,
          tax: parseProductTaxIds(product.taxes),
          unit_type: product.unit_id,
          downloadable: product.downloadable === 1,
          allow_purchase: product.allow_purchase === 1,
          sku: product.sku,
          description: product.description,
        };
        if (res.data.categories) setCategories(res.data.categories);
        if (res.data.subCategories) setSubCategories(res.data.subCategories);
        if (res.data.taxes) setTaxes(res.data.taxes);
        if (res.data.unit_types) setUnitTypes(res.data.unit_types);

        form.setFieldsValue(formData);

        setExistingImages(
          (Array.isArray(product.files) ? product.files : [])
            .filter((file) => file?.file_url || file?.external_link)
            .map((file) => ({
              id: file.id,
              url: file.file_url || file.external_link,
              isDefault: Boolean(file.default_status),
            })),
        );

        if (product.downloadable && (product.download_file_url || product.downloadable_file)) {
          setExistingDownloadFile({
            url: product.download_file_url,
            name: product.downloadable_file || 'فایل محصول',
          });
          setDeleteDownloadableMarked(false);
        } else {
          setExistingDownloadFile(null);
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      message.error('خطا در دریافت اطلاعات محصول');
      navigate('/products');
    } finally {
      setFetching(false);
    }
  };

  // ============ Category Management ============

  const openCategoryModal = async () => {
    setCategoryModalVisible(true);
    setLoadingCategoryList(true);
    try {
      const res = await productCategoriesApi.getCreateData();
      if (res?.status === 'success' && res?.data) {
        setCategoryListForModal(res.data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategoryList(false);
    }
  };

  const handleAddCategory = async (values) => {
    setCategorySubmitting(true);
    try {
      await productCategoriesApi.create(values.category_name);
      message.success('دسته‌بندی با موفقیت ایجاد شد');
      categoryForm.resetFields();
      const refreshRes = await productCategoriesApi.getCreateData();
      if (refreshRes?.status === 'success' && refreshRes?.data) {
        setCategoryListForModal(refreshRes.data.categories || []);
        setCategories(refreshRes.data.categories || []);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      message.error('خطا در ایجاد دسته‌بندی');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleEditCategory = async (catId) => {
    if (!editingCategoryName.trim()) {
      message.warning('نام دسته‌بندی نمی‌تواند خالی باشد');
      return;
    }
    setEditCategorySaving(true);
    try {
      await productCategoriesApi.update(catId, editingCategoryName.trim());
      message.success('دسته‌بندی با موفقیت ویرایش شد');
      setEditingCategoryId(null);
      setEditingCategoryName('');
      const refreshRes = await productCategoriesApi.getCreateData();
      if (refreshRes?.status === 'success' && refreshRes?.data) {
        setCategoryListForModal(refreshRes.data.categories || []);
        setCategories(refreshRes.data.categories || []);
      }
    } catch (error) {
      console.error('Error editing category:', error);
      message.error('خطا در ویرایش دسته‌بندی');
    } finally {
      setEditCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (catId) => {
    try {
      await productCategoriesApi.delete(catId);
      message.success('دسته‌بندی با موفقیت حذف شد');
      const refreshRes = await productCategoriesApi.getCreateData();
      if (refreshRes?.status === 'success' && refreshRes?.data) {
        setCategoryListForModal(refreshRes.data.categories || []);
        setCategories(refreshRes.data.categories || []);
      }
      const currentCat = form.getFieldValue('category_id');
      if (currentCat === catId) {
        form.setFieldsValue({ category_id: undefined, sub_category_id: undefined });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('خطا در حذف دسته‌بندی');
    }
  };

  const handleCategoryModalClose = () => {
    setCategoryModalVisible(false);
    categoryForm.resetFields();
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  // ============ Subcategory Management ============

  const openSubcategoryModal = async () => {
    setSubcategoryModalVisible(true);
    setLoadingSubcategoryList(true);
    try {
      const res = await productSubCategoriesApi.getCreateData();
      if (res?.status === 'success' && res?.data) {
        setCategoryListForModal(res.data.categories || []);
      }
      const currentCat = form.getFieldValue('category_id');
      if (currentCat) {
        subcategoryForm.setFieldsValue({ category_id: currentCat });
        const subRes = await productSubCategoriesApi.getByCategory(currentCat);
        if (subRes?.status === 'success') {
          setSubcategoryListForModal(subRes.subcategories || []);
        }
      }
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setLoadingSubcategoryList(false);
    }
  };

  const handleSubcategoryCategoryChange = async (categoryId) => {
    if (!categoryId) {
      setSubcategoryListForModal([]);
      return;
    }
    setLoadingSubcategoryList(true);
    try {
      const res = await productSubCategoriesApi.getByCategory(categoryId);
      if (res?.status === 'success') {
        setSubcategoryListForModal(res.subcategories || []);
      }
    } catch (error) {
      console.error('Error loading subcategories for category:', error);
    } finally {
      setLoadingSubcategoryList(false);
    }
  };

  const handleAddSubcategory = async (values) => {
    setSubcategorySubmitting(true);
    try {
      await productSubCategoriesApi.create(values.category_id, values.category_name);
      message.success('زیردسته‌بندی با موفقیت ایجاد شد');
      subcategoryForm.setFieldsValue({ category_name: '' });
      const subRes = await productSubCategoriesApi.getByCategory(values.category_id);
      if (subRes?.status === 'success') {
        setSubcategoryListForModal(subRes.subcategories || []);
        const mainCat = form.getFieldValue('category_id');
        if (mainCat === values.category_id) {
          setSubCategories(prev => {
            const newSubs = subRes.subcategories || [];
            const otherSubs = prev.filter(s => s.category_id !== mainCat);
            return [...otherSubs, ...newSubs];
          });
        }
      }
    } catch (error) {
      console.error('Error adding subcategory:', error);
      message.error('خطا در ایجاد زیردسته‌بندی');
    } finally {
      setSubcategorySubmitting(false);
    }
  };

  const handleEditSubcategory = async (subId) => {
    if (!editingSubcategoryName.trim()) {
      message.warning('نام زیردسته‌بندی نمی‌تواند خالی باشد');
      return;
    }
    setEditSubcategorySaving(true);
    try {
      const catId = subcategoryForm.getFieldValue('category_id');
      await productSubCategoriesApi.update(subId, { 
        category_name: editingSubcategoryName.trim(),
        category_id: catId,
      });
      message.success('زیردسته‌بندی با موفقیت ویرایش شد');
      setEditingSubcategoryId(null);
      setEditingSubcategoryName('');
      if (catId) {
        const subRes = await productSubCategoriesApi.getByCategory(catId);
        if (subRes?.status === 'success') {
          setSubcategoryListForModal(subRes.subcategories || []);
          const mainCat = form.getFieldValue('category_id');
          if (mainCat === catId) {
            setSubCategories(prev => {
              const newSubs = subRes.subcategories || [];
              const otherSubs = prev.filter(s => s.category_id !== mainCat);
              return [...otherSubs, ...newSubs];
            });
          }
        }
      }
    } catch (error) {
      console.error('Error editing subcategory:', error);
      message.error('خطا در ویرایش زیردسته‌بندی');
    } finally {
      setEditSubcategorySaving(false);
    }
  };

  const handleDeleteSubcategory = async (subId) => {
    try {
      await productSubCategoriesApi.delete(subId);
      message.success('زیردسته‌بندی با موفقیت حذف شد');
      const catId = subcategoryForm.getFieldValue('category_id');
      if (catId) {
        const subRes = await productSubCategoriesApi.getByCategory(catId);
        if (subRes?.status === 'success') {
          setSubcategoryListForModal(subRes.subcategories || []);
          const mainCat = form.getFieldValue('category_id');
          if (mainCat === catId) {
            setSubCategories(prev => {
              const newSubs = subRes.subcategories || [];
              const otherSubs = prev.filter(s => s.category_id !== mainCat);
              return [...otherSubs, ...newSubs];
            });
          }
        }
      }
      const currentSub = form.getFieldValue('sub_category_id');
      if (currentSub === subId) {
        form.setFieldsValue({ sub_category_id: undefined });
      }
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      message.error('خطا در حذف زیردسته‌بندی');
    }
  };

  const handleSubcategoryModalClose = () => {
    setSubcategoryModalVisible(false);
    subcategoryForm.resetFields();
    setEditingSubcategoryId(null);
    setEditingSubcategoryName('');
    setSubcategoryListForModal([]);
  };

  // ============ Product Submit ============

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const validatedValues = await form.validateFields();

      const imageFiles = getUploadFiles(imageFileList);
      const downloadableFile = getUploadFile(downloadableFileList);
      const payload = buildProductPayload(validatedValues, {
        imageFiles,
        downloadableFile,
        deleteDownloadableFile: deleteDownloadableMarked && !downloadableFile,
      });

      await productsApi.update(id, payload);
      message.success('محصول با موفقیت ویرایش شد');
      navigate('/products');
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ویرایش محصول');
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const handleMarkDeleteDownloadable = () => {
    setDeleteDownloadableMarked(true);
    setExistingDownloadFile(null);
    setDownloadableFileList([]);
    form.setFieldsValue({ downloadable: false });
  };

  const handleDeleteExistingImage = async (fileId) => {
    if (!fileId) {
      message.error('شناسه فایل تصویر یافت نشد');
      return;
    }
    setDeletingImageId(fileId);
    try {
      await productsApi.destroyFile(fileId);
      message.success('تصویر با موفقیت حذف شد');
      setExistingImages((prev) => prev.filter((img) => img.id !== fileId));
    } catch (error) {
      console.error('Error deleting image:', error);
      message.error('خطا در حذف تصویر');
    } finally {
      setDeletingImageId(null);
    }
  };

  // Filter subcategories based on selected category
  const selectedCategory = Form.useWatch('category_id', form);
  const filteredSubcategories = selectedCategory
    ? subCategories.filter(sub => sub.category_id === selectedCategory)
    : subCategories;

  // When category changes, load subcategories
  const handleMainCategoryChange = async (catId) => {
    form.setFieldsValue({ sub_category_id: undefined });
    if (catId) {
      setLoadingSubcategories(true);
      try {
        const res = await productSubCategoriesApi.getByCategory(catId);
        if (res?.status === 'success') {
          setSubCategories(prev => {
            const newSubs = res.subcategories || [];
            const otherSubs = prev.filter(s => s.category_id !== catId);
            return [...otherSubs, ...newSubs];
          });
        }
      } catch (err) {
        console.warn('Could not load subcategories:', err);
      } finally {
        setLoadingSubcategories(false);
      }
    }
  };

  if (fetching) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
            <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                  <ShoppingOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                    ویرایش محصول
                  </h1>
                </div>
              </div>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/products')}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
              <FormSkeleton />
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <ShoppingOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                  ویرایش محصول
                </h1>
              </div>
            </div>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={() => navigate('/products')}
              style={{ fontFamily: 'CustomFont' }}
            >
              بازگشت
            </Button>
          </div>

          {/* Form */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                style={{ fontFamily: 'CustomFont' }}
              >
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>نام</span>}
                    name="name"
                    rules={[{ required: true, message: 'لطفا نام محصول را وارد کنید' }]}
                  >
                    <Input placeholder="مثلاً: میزبانی وب، لپ تاپ" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>قیمت</span>}
                    name="price"
                    rules={[{ required: true, message: 'لطفا قیمت را وارد کنید' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                      placeholder="0"
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی محصول</span>}
                    name="category_id"
                  >
                    <div className="flex gap-2">
                      <Select
                        placeholder="انتخاب دسته‌بندی"
                        style={{ flex: 1, fontFamily: 'CustomFont' }}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                        value={form.getFieldValue('category_id')}
                        onChange={(val) => {
                          form.setFieldsValue({ category_id: val });
                          handleMainCategoryChange(val);
                        }}
                      >
                        {categories.map((cat) => (
                          <Option key={cat.id} value={cat.id}>{cat.category_name}</Option>
                        ))}
                      </Select>
                      <Tooltip title="مدیریت دسته‌بندی‌ها">
                        <Button icon={<PlusOutlined />} onClick={openCategoryModal} className="flex-shrink-0" />
                      </Tooltip>
                    </div>
                  </Form.Item>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>زیرشاخه محصول</span>}
                    name="sub_category_id"
                  >
                    <div className="flex gap-2">
                      <Select
                        placeholder={loadingSubcategories ? "در حال بارگذاری..." : (selectedCategory ? "انتخاب زیردسته" : "ابتدا دسته‌بندی انتخاب کنید")}
                        style={{ flex: 1, fontFamily: 'CustomFont' }}
                        allowClear
                        disabled={!selectedCategory || loadingSubcategories}
                        loading={loadingSubcategories}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                        value={form.getFieldValue('sub_category_id')}
                        onChange={(val) => form.setFieldsValue({ sub_category_id: val })}
                      >
                        {filteredSubcategories.map((sub) => (
                          <Option key={sub.id} value={sub.id}>{sub.category_name}</Option>
                        ))}
                      </Select>
                      <Tooltip title="مدیریت زیردسته‌بندی‌ها">
                        <Button icon={<PlusOutlined />} onClick={openSubcategoryModal} disabled={!selectedCategory} className="flex-shrink-0" />
                      </Tooltip>
                    </div>
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>مالیات</span>}
                    name="tax"
                  >
                    <Select mode="multiple" placeholder="هیچ موردی انتخاب نشده است" style={{ width: '100%', fontFamily: 'CustomFont' }} allowClear>
                      {taxes.map((tax) => (
                        <Option key={tax.id} value={Number(tax.id)}>{tax.tax_name} ({tax.rate_percent}%)</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>نوع واحد</span>}
                    name="unit_type"
                    rules={[{ required: true, message: 'لطفا نوع واحد را انتخاب کنید' }]}
                  >
                    <Select placeholder="انتخاب کنید" style={{ fontFamily: 'CustomFont' }}>
                      {unitTypes.map((unit) => (
                        <Option key={unit.id} value={unit.id}>{unit.unit_type}</Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>شناسه محصول (SKU)</span>}
                    name="sku"
                  >
                    <Input placeholder="شناسه محصول (SKU) را اینجا وارد کنید" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>
                </div>

                {/* Switches */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Item
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>
                        قابل دانلود
                        <QuestionCircleOutlined className="mr-2 text-gray-400" title="اگر محصول قابل دانلود است، این گزینه را فعال کنید" />
                      </span>
                    }
                    name="downloadable"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="بله" unCheckedChildren="خیر" />
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>امکان خرید برای مشتری فعال است</span>}
                    name="allow_purchase"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="بله" unCheckedChildren="خیر" />
                  </Form.Item>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}
                    name="description"
                  >
                    <TextArea rows={4} placeholder="توضیحات محصول" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>
                </div>

                {/* Image Upload */}
                <div className="mb-4">
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>تصاویر محصول</span>}
                    extra={
                      <span style={{ fontFamily: 'CustomFont' }} className="text-xs text-gray-500">
                        می‌توانید چند تصویر محصول بارگذاری کنید.
                      </span>
                    }
                  >
                    {existingImages.length > 0 && (
                      <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {existingImages.map((image) => (
                          <div
                            key={image.id}
                            className="relative p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                          >
                            <img
                              src={image.url}
                              alt="تصویر محصول"
                              className="h-24 w-full rounded-lg object-contain border bg-white"
                            />
                            {image.isDefault && (
                              <span
                                className="absolute top-3 right-3 text-[10px] px-1.5 py-0.5 rounded bg-teal-600 text-white"
                                style={{ fontFamily: 'CustomFont' }}
                              >
                                پیش‌فرض
                              </span>
                            )}
                            <Popconfirm
                              title="حذف تصویر"
                              description="آیا از حذف این تصویر اطمینان دارید؟"
                              onConfirm={() => handleDeleteExistingImage(image.id)}
                              okText="بله"
                              cancelText="خیر"
                            >
                              <Button
                                danger
                                size="small"
                                block
                                className="mt-2"
                                icon={<DeleteOutlined />}
                                loading={deletingImageId === image.id}
                                style={{ fontFamily: 'CustomFont' }}
                              >
                                حذف
                              </Button>
                            </Popconfirm>
                          </div>
                        ))}
                      </div>
                    )}
                    <Upload.Dragger
                      multiple
                      maxCount={10}
                      accept="image/*"
                      listType="picture"
                      fileList={imageFileList}
                      onChange={({ fileList: nextList }) => setImageFileList(nextList)}
                      beforeUpload={() => false}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                      <p className="ant-upload-text" style={{ fontFamily: 'CustomFont' }}>
                        {existingImages.length ? 'افزودن تصاویر جدید' : 'تصاویر را انتخاب کنید یا اینجا رها کنید'}
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                </div>

                {/* Downloadable File Upload */}
                {isDownloadable && (
                  <div className="mb-4">
                    <Form.Item
                      label={<span style={{ fontFamily: 'CustomFont' }}>فایل قابل دانلود</span>}
                    >
                      {existingDownloadFile && (
                        <div className="mb-3 flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                          <a
                            href={existingDownloadFile.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 truncate"
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            {existingDownloadFile.name}
                          </a>
                          <Popconfirm
                            title="حذف فایل"
                            description="فایل در زمان ذخیره حذف می‌شود. می‌توانید فایل جدید جایگزین کنید."
                            onConfirm={handleMarkDeleteDownloadable}
                            okText="بله"
                            cancelText="خیر"
                          >
                            <Button
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              style={{ fontFamily: 'CustomFont' }}
                            >
                              حذف فایل
                            </Button>
                          </Popconfirm>
                        </div>
                      )}
                      <Upload.Dragger
                        multiple={false}
                        maxCount={1}
                        fileList={downloadableFileList}
                        onChange={({ fileList: nextList }) => {
                          setDownloadableFileList(nextList);
                          if (nextList.length) setDeleteDownloadableMarked(false);
                        }}
                        beforeUpload={() => false}
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                        <p className="ant-upload-text" style={{ fontFamily: 'CustomFont' }}>
                          {existingDownloadFile ? 'آپلود فایل جدید (جایگزین فایل قبلی)' : 'فایل دانلود محصول را انتخاب کنید'}
                        </p>
                      </Upload.Dragger>
                    </Form.Item>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-4">
                  <Button icon={<CloseOutlined />} onClick={handleCancel} size="middle" style={{ fontFamily: 'CustomFont', minWidth: '120px' }}>
                    لغو
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    size="middle"
                    className="bg-green-600 hover:bg-green-700 border-green-600"
                    style={{ fontFamily: 'CustomFont', minWidth: '120px' }}
                  >
                    ذخیره تغییرات
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </Card>

        {/* ============ Category Management Modal ============ */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>مدیریت دسته‌بندی محصولات</span>}
          open={categoryModalVisible}
          onCancel={handleCategoryModalClose}
          footer={null}
          width={550}
          style={{ fontFamily: 'CustomFont' }}
          destroyOnClose
        >
          <Form
            form={categoryForm}
            layout="inline"
            onFinish={handleAddCategory}
            style={{ fontFamily: 'CustomFont', marginBottom: '16px' }}
            className="w-full"
          >
            <div className="flex gap-2 w-full">
              <Form.Item name="category_name" rules={[{ required: true, message: 'نام را وارد کنید' }]} className="flex-1 mb-0">
                <Input placeholder="نام دسته‌بندی جدید" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
              <Form.Item className="mb-0">
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={categorySubmitting} className="bg-green-600 hover:bg-green-700 border-green-600" style={{ fontFamily: 'CustomFont' }}>
                  افزودن
                </Button>
              </Form.Item>
            </div>
          </Form>

          <Divider className="my-3" />

          <Spin spinning={loadingCategoryList}>
            {categoryListForModal.length === 0 ? (
              <Empty description={<span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی‌ای وجود ندارد</span>} />
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <List
                  size="small"
                  dataSource={categoryListForModal}
                  renderItem={(cat) => (
                    <List.Item
                      style={{ fontFamily: 'CustomFont', padding: '8px 12px' }}
                      actions={
                        editingCategoryId === cat.id
                          ? [
                              <Button key="save" type="text" icon={<CheckOutlined />} size="small" className="text-green-600" loading={editCategorySaving} onClick={() => handleEditCategory(cat.id)} />,
                              <Button key="cancel" type="text" icon={<CloseCircleOutlined />} size="small" disabled={editCategorySaving} className="text-gray-400" onClick={() => { setEditingCategoryId(null); setEditingCategoryName(''); }} />,
                            ]
                          : [
                              <Button key="edit" type="text" icon={<EditOutlined />} size="small" className="text-blue-600" onClick={() => { setEditingCategoryId(cat.id); setEditingCategoryName(cat.category_name); }} />,
                              <Popconfirm key="delete" title="آیا از حذف مطمئن هستید؟" onConfirm={() => handleDeleteCategory(cat.id)} okText="بله" cancelText="خیر">
                                <Button type="text" icon={<DeleteOutlined />} size="small" className="text-red-500" />
                              </Popconfirm>,
                            ]
                      }
                    >
                      {editingCategoryId === cat.id ? (
                        <Input value={editingCategoryName} onChange={(e) => setEditingCategoryName(e.target.value)} size="small" style={{ fontFamily: 'CustomFont', maxWidth: '250px' }} onPressEnter={() => handleEditCategory(cat.id)} autoFocus />
                      ) : (
                        <span style={{ fontFamily: 'CustomFont' }}>{cat.category_name}</span>
                      )}
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Spin>
        </Modal>

        {/* ============ Subcategory Management Modal ============ */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>مدیریت زیردسته‌بندی محصولات</span>}
          open={subcategoryModalVisible}
          onCancel={handleSubcategoryModalClose}
          footer={null}
          width={550}
          style={{ fontFamily: 'CustomFont' }}
          destroyOnClose
        >
          <Form form={subcategoryForm} layout="vertical" onFinish={handleAddSubcategory} style={{ fontFamily: 'CustomFont' }}>
            <Form.Item label={<span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی والد</span>} name="category_id" rules={[{ required: true, message: 'دسته‌بندی والد را انتخاب کنید' }]}>
              <Select
                placeholder="انتخاب دسته‌بندی"
                style={{ fontFamily: 'CustomFont' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={handleSubcategoryCategoryChange}
              >
                {(categoryListForModal.length > 0 ? categoryListForModal : categories).map((cat) => (
                  <Option key={cat.id} value={cat.id}>{cat.category_name}</Option>
                ))}
              </Select>
            </Form.Item>

            <div className="flex gap-2">
              <Form.Item name="category_name" rules={[{ required: true, message: 'نام را وارد کنید' }]} className="flex-1 mb-3">
                <Input placeholder="نام زیردسته‌بندی جدید" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
              <Form.Item className="mb-3">
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={subcategorySubmitting} className="bg-green-600 hover:bg-green-700 border-green-600" style={{ fontFamily: 'CustomFont' }}>
                  افزودن
                </Button>
              </Form.Item>
            </div>
          </Form>

          <Divider className="my-3" />

          <Spin spinning={loadingSubcategoryList}>
            {subcategoryListForModal.length === 0 ? (
              <Empty description={<span style={{ fontFamily: 'CustomFont' }}>زیردسته‌بندی‌ای وجود ندارد</span>} />
            ) : (
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <List
                  size="small"
                  dataSource={subcategoryListForModal}
                  renderItem={(sub) => (
                    <List.Item
                      style={{ fontFamily: 'CustomFont', padding: '8px 12px' }}
                      actions={
                        editingSubcategoryId === sub.id
                          ? [
                              <Button key="save" type="text" icon={<CheckOutlined />} size="small" className="text-green-600" loading={editSubcategorySaving} onClick={() => handleEditSubcategory(sub.id)} />,
                              <Button key="cancel" type="text" icon={<CloseCircleOutlined />} size="small" disabled={editSubcategorySaving} className="text-gray-400" onClick={() => { setEditingSubcategoryId(null); setEditingSubcategoryName(''); }} />,
                            ]
                          : [
                              <Button key="edit" type="text" icon={<EditOutlined />} size="small" className="text-blue-600" onClick={() => { setEditingSubcategoryId(sub.id); setEditingSubcategoryName(sub.category_name); }} />,
                              <Popconfirm key="delete" title="آیا از حذف مطمئن هستید؟" onConfirm={() => handleDeleteSubcategory(sub.id)} okText="بله" cancelText="خیر">
                                <Button type="text" icon={<DeleteOutlined />} size="small" className="text-red-500" />
                              </Popconfirm>,
                            ]
                      }
                    >
                      {editingSubcategoryId === sub.id ? (
                        <Input value={editingSubcategoryName} onChange={(e) => setEditingSubcategoryName(e.target.value)} size="small" style={{ fontFamily: 'CustomFont', maxWidth: '250px' }} onPressEnter={() => handleEditSubcategory(sub.id)} autoFocus />
                      ) : (
                        <span style={{ fontFamily: 'CustomFont' }}>{sub.category_name}</span>
                      )}
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Spin>
        </Modal>

        {/* Tax Modal */}
        <Modal
          title="افزودن مالیات جدید"
          open={taxModalVisible}
          onCancel={() => { setTaxModalVisible(false); taxForm.resetFields(); }}
          footer={null}
          style={{ fontFamily: 'CustomFont' }}
        >
          <Form form={taxForm} layout="vertical" onFinish={() => {}} style={{ fontFamily: 'CustomFont' }}>
            <Form.Item label={<span style={{ fontFamily: 'CustomFont' }}>نام مالیات</span>} name="name" rules={[{ required: true }]}>
              <Input style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
            <Form.Item label={<span style={{ fontFamily: 'CustomFont' }}>نرخ مالیات (%)</span>} name="rate" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} max={100} formatter={(value) => `${value}%`} parser={(value) => value.replace('%', '')} />
            </Form.Item>
            <Form.Item className="mb-0">
              <Space>
                <Button onClick={() => { setTaxModalVisible(false); taxForm.resetFields(); }}>لغو</Button>
                <Button type="primary" htmlType="submit" className="bg-green-600 hover:bg-green-700">افزودن</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default EditProduct;
